import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/files";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { CnasAddDialog } from "@/components/CnasAddDialog";
import { BadgeCheck, Eye, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUT = {
  valide: ["Valide", "bg-emerald-100 text-emerald-700"],
  bientot: ["Expire bientôt", "bg-amber-100 text-amber-700"],
  expiree: ["Expirée", "bg-red-100 text-red-700"],
};

const Badge = ({ a }) => {
  const [l, c] = STATUT[a.statut];
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${c}`} data-testid={`cnas-statut-${a.id}`}>{l}{a.statut !== "expiree" ? ` · ${a.jours_restants} j` : ""}</span>;
};

const Row = ({ a, editable, onDelete }) => (
  <tr data-testid={`cnas-row-${a.id}`}>
    <td className="py-2 pr-2">{fmtDate(a.date_delivrance)}</td>
    <td className="py-2 pr-2 font-medium">{fmtDate(a.date_validite)}</td>
    <td className="py-2 pr-2">{a.numero_employeur}</td>
    <td className="py-2 pr-2">{a.effectif ?? "—"}</td>
    <td className="py-2 pr-2 text-xs">{a.reference}</td>
    <td className="py-2 pr-2"><Badge a={a} /></td>
    <td className="py-2 text-right whitespace-nowrap">
      {a.file_id && <Button size="sm" variant="outline" className="gap-1" onClick={() => window.open(fileUrl(a.file_id), "_blank")} data-testid={`cnas-view-${a.id}`}><Eye className="h-4 w-4" /> Voir</Button>}
      {editable && <Button size="sm" variant="ghost" onClick={() => onDelete(a)} data-testid={`cnas-delete-${a.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
    </td>
  </tr>
);

const SocieteCnas = ({ s, items, editable, onAdd, onDelete }) => (
  <div className="rounded-xl border bg-card p-5 mb-5" data-testid={`cnas-societe-${s.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-primary" /><h3 className="font-heading font-bold">{s.name}</h3>
        {items[0] ? <Badge a={items[0]} /> : <span className="text-xs text-muted-foreground">Aucune attestation</span>}</div>
      {editable && <Button size="sm" className="gap-1" onClick={() => onAdd(s, items[0])} data-testid={`cnas-add-${s.id}`}><Plus className="h-4 w-4" /> Ajouter une attestation</Button>}
    </div>
    {items.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-left text-muted-foreground border-b">
            {["Délivrée le", "Valable jusqu'au", "N° employeur", "Effectif", "N° document", "Statut"].map((h) => <th key={h} className="py-2 pr-2 font-medium">{h}</th>)}
            <th />
          </tr></thead>
          <tbody className="divide-y">{items.map((a) => <Row key={a.id} a={a} editable={editable} onDelete={onDelete} />)}</tbody>
        </table>
      </div>
    )}
  </div>
);

export default function Cnas() {
  const { societes, societeId } = useApp();
  const { role } = useAuth();
  const editable = role === "administrateur" || role === "comptabilite";
  const [items, setItems] = useState(null);
  const [add, setAdd] = useState(null);
  const socs = societes.filter((s) => (societeId === "all" ? !/GENERAL FOOD/i.test(s.name) : s.id === societeId));

  const load = () => api.get("/cnas").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onDelete = async (a) => {
    if (!window.confirm("Supprimer cette attestation ?")) return;
    await api.delete(`/cnas/${a.id}`);
    toast.success("Attestation supprimée");
    load();
  };

  return (
    <div data-testid="cnas-page">
      <PageHeader title="Mise à jour CNAS" subtitle="Attestations de mise à jour des cotisations de sécurité sociale, par société" />
      {items === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : socs.map((s) => <SocieteCnas key={s.id} s={s} items={items.filter((a) => a.societe_id === s.id)} editable={editable}
          onAdd={(soc, last) => setAdd({ soc, last })} onDelete={onDelete} />)}
      {add && <CnasAddDialog societe={add.soc} last={add.last} onClose={() => setAdd(null)} onSaved={() => { setAdd(null); load(); }} />}
    </div>
  );
}
